import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../ProgressBar';
import { AlgorithmProvider, getAlgorithmDisplayName } from '../../utils/algorithmProvider';
import { FavoriteSnapshot } from '../../services/favoritesService';
import { FavoriteButton } from '../FavoriteButton';
import { API_BASE_URL } from '../../services/api';

// 各アルゴリズムが必要とする最小日数を計算
interface DetectedStock {
  symbol: string;
  company?: string;
  confidence_score: number;
  change_date: string;
  stage: '確定' | '兆候';
  price_gain_percent?: number;
  detection_reason?: string;
  metrics?: any;
  algorithm?: string;
  isNew?: boolean;  // 新規検出フラグ
  current_price?: number;  // 最新株価
  parameters?: any;  // 使用されたアルゴリズムパラメータ
  required_days?: number;  // 検出に必要な日数
}

interface DetectionSummary {
  total_stocks: number;
  processed_stocks: number;
  detected_count: number;
  processing_time: number;
  success_rate?: number;
}

interface DataRange {
  status: string;
  oldest_date: string | null;
  latest_date: string | null;
  days_covered: number;
  message: string;
  trading_days_available?: number | null;  // 実際の営業日数（データポイント数）
  reference_symbol?: string | null;        // 参照銘柄（AAPL）
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // 初期化完了フラグ
  const [algorithms, setAlgorithms] = useState<any[]>([]); // アルゴリズム一覧を状態管理
  const [sortBy, setSortBy] = useState<'confidence' | 'gain' | 'date' | 'symbol' | 'price'>('confidence');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [stageFilter, setStageFilter] = useState<'all' | 'confirmed' | 'indication'>('all');
  const [algorithmFilter, setAlgorithmFilter] = useState<string[]>(() => {
    const saved = localStorage.getItem('algorithmFilter');
    if (saved) {
      return JSON.parse(saved);
    }
    // 初回訪問時は全アルゴリズムを選択（AlgorithmProviderはまだ利用不可なので空配列）
    return [];
  });

  // プログレス状態
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [currentStock, setCurrentStock] = useState<string>('');
  
  // 実際の検出結果データ
  const [detectedStocks, setDetectedStocks] = useState<DetectedStock[]>([]);
  const [detectionSummary, setDetectionSummary] = useState<DetectionSummary | null>(null);
  const [lastDetectionTime, setLastDetectionTime] = useState<string | null>(null);
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<string[]>(['linear_uptrend']);
  
  // データ期間表示機能
  const [dataRange, setDataRange] = useState<DataRange | null>(null);
  const [dataRangeError, setDataRangeError] = useState<string>('');
  
  // 手動更新機能
  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<string>('');
  
  // 期間情報更新機能
  const [isRefreshingRange, setIsRefreshingRange] = useState(false);
  
  // アルゴリズム名の表示名変換（アルゴリズムプロバイダ経由）

  // アルゴリズム選択の処理（検出実行用）
  const handleAlgorithmChange = (algorithm: string, checked: boolean) => {
    if (checked) {
      setSelectedAlgorithms(prev => [...prev, algorithm]);
    } else {
      setSelectedAlgorithms(prev => prev.filter(alg => alg !== algorithm));
    }
  };

  // アルゴリズムフィルタの処理（表示フィルタ用）
  const handleAlgorithmFilterChange = (algorithm: string, checked: boolean) => {
    if (checked) {
      setAlgorithmFilter(prev => [...prev, algorithm]);
    } else {
      setAlgorithmFilter(prev => prev.filter(alg => alg !== algorithm));
    }
  };

  // 初回ロード時に保存された検出結果を復元
  useEffect(() => {
    const loadSavedResults = async () => {
      // バックエンドからアルゴリズム定義を取得
      await AlgorithmProvider.fetchAlgorithmsFromBackend();

      // アルゴリズム一覧を状態に保存
      const allAlgorithms = AlgorithmProvider.getAllAlgorithms();
      setAlgorithms(allAlgorithms);

      try {
        const savedStocks = localStorage.getItem('detectedStocks');
        const savedSummary = localStorage.getItem('detectionSummary');
        const savedTime = localStorage.getItem('lastDetectionTime');
        const savedAlgorithms = localStorage.getItem('selectedAlgorithms');

        if (savedStocks) {
          setDetectedStocks(JSON.parse(savedStocks));
        }
        if (savedSummary) {
          setDetectionSummary(JSON.parse(savedSummary));
        }
        if (savedTime) {
          setLastDetectionTime(savedTime);
        }
        if (savedAlgorithms) {
          const parsed = JSON.parse(savedAlgorithms);
          // 有効なアルゴリズムのみにフィルタリング
          const validAlgorithms = allAlgorithms.map(a => a.key);
          const filtered = parsed.filter((algo: string) => validAlgorithms.includes(algo));

          // 無効なアルゴリズムが含まれていた場合はログ出力
          const invalid = parsed.filter((algo: string) => !validAlgorithms.includes(algo));
          if (invalid.length > 0) {
            console.warn(`無効なアルゴリズムを除外しました: ${invalid.join(', ')}`);
          }

          // フィルタリング後のアルゴリズムが0個の場合はデフォルト値を使用
          if (filtered.length > 0) {
            setSelectedAlgorithms(filtered);
          } else {
            console.warn('有効なアルゴリズムが0個のため、デフォルト値を使用します');
          }
        }

        // フィルタ設定を復元
        const savedFilter = localStorage.getItem('stageFilter');
        if (savedFilter) {
          setStageFilter(savedFilter as typeof stageFilter);
        }

        // ソート設定を復元
        const savedSort = localStorage.getItem('sortBy');
        if (savedSort) {
          setSortBy(savedSort as typeof sortBy);
        }

        const savedSortOrder = localStorage.getItem('sortOrder');
        if (savedSortOrder) {
          setSortOrder(savedSortOrder as typeof sortOrder);
        }

        // 初回訪問時のアルゴリズムフィルタ初期化
        // （localStorageに保存されていない場合のみ）
        const savedAlgorithmFilter = localStorage.getItem('algorithmFilter');
        if (!savedAlgorithmFilter) {
          // 初回訪問時は全アルゴリズムを選択状態にする
          const allAlgorithmKeys = AlgorithmProvider.getAllAlgorithms().map(a => a.key);
          setAlgorithmFilter(allAlgorithmKeys);
        }

        // 初期化完了をマーク
        setIsInitialized(true);
      } catch (error) {
        console.error('保存された検出結果の読み込みエラー:', error);
        setIsInitialized(true);
      }
    };

    loadSavedResults();
    fetchDataRange();
  }, []);

  // データ期間情報を取得する関数
  const fetchDataRange = async () => {
    setDataRangeError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/data/date-range`);
      
      if (response.ok) {
        const data = await response.json();
        setDataRange(data);
      } else {
        const errorMsg = `APIエラー: ステータス ${response.status}`;
        setDataRangeError(errorMsg);
      }
    } catch (error) {
      let errorMsg = 'データ期間の取得に失敗しました';
      
      if (error instanceof Error) {
        errorMsg = `エラー: ${error.message}`;
      }
      
      setDataRangeError(errorMsg);
    }
  };

  // 期間情報のみを更新する関数
  const handleRefreshDataRange = async () => {
    setIsRefreshingRange(true);
    setDataRangeError('');
    
    try {
      await fetchDataRange();
    } catch (error) {
      console.error('期間情報更新エラー:', error);
    } finally {
      setIsRefreshingRange(false);
    }
  };

  // 手動データ更新関数
  const handleManualDataUpdate = async () => {
    setIsUpdatingData(true);
    setUpdateMessage('データ更新処理を開始しています...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/data/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'started') {
          setUpdateMessage('データ更新がバックグラウンドで開始されました。完了まで数分かかる場合があります...');
          
          // バックグラウンド処理の完了を監視
          startDataUpdatePolling();
        } else {
          setUpdateMessage(`予期しないレスポンス: ${result.message || '不明なエラー'}`);
          setIsUpdatingData(false);
        }
      } else {
        const error = await response.json();
        setUpdateMessage(`エラー: ${error.error || 'データ更新に失敗しました'}`);
        setIsUpdatingData(false);
      }
    } catch (error) {
      let errorMsg = 'データ更新エラー';
      
      if (error instanceof Error) {
        errorMsg = `エラー: ${error.message}`;
      }
      
      setUpdateMessage(errorMsg);
      setIsUpdatingData(false);
    }
  };

  // データ更新完了をポーリングで監視
  const startDataUpdatePolling = () => {
    let currentInterval = 10000; // 初期間隔: 10秒
    let pollInterval: NodeJS.Timeout;
    
    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/data/fetch/status`);
        if (response.ok) {
          const status = await response.json();
          
          if (!status.is_running) {
            // 処理完了
            clearInterval(pollInterval);
            
            if (status.error) {
              setUpdateMessage(`データ更新エラー: ${status.error}`);
              setIsUpdatingData(false);
            } else if (status.results) {
              const results = status.results;
              const message = `データ更新完了！${results.success}/${results.total} 銘柄を処理 (成功率: ${results.success_rate.toFixed(1)}%)`;
              setUpdateMessage(message);
              setIsUpdatingData(false);
              
              // データ期間情報を再取得
              setTimeout(() => {
                fetchDataRange();
              }, 1000);
            } else {
              setUpdateMessage('データ更新が完了しましたが、結果の取得に失敗しました。');
              setIsUpdatingData(false);
            }
          } else {
            // まだ処理中
            if (status.progress) {
              const progress = status.progress;
              setUpdateMessage(
                `処理中: ${progress.processed}/${progress.total} 銘柄 (${progress.percentage.toFixed(1)}%)`
              );
              
              // 90%を超えたら監視頻度を上げる
              if (progress.percentage > 90 && currentInterval > 3000) {
                currentInterval = 3000;
                clearInterval(pollInterval);
                pollInterval = setInterval(poll, currentInterval);
              }
            } else {
              setUpdateMessage('データ更新処理中です。しばらくお待ちください...');
            }
          }
        }
      } catch (error) {
        console.error('ポーリングエラー:', error);
        setUpdateMessage('状況確認中にエラーが発生しました。');
      }
    };
    
    poll(); // 即座に1回実行
    pollInterval = setInterval(poll, currentInterval);
  };

  // フィルタ設定の変更時に保存（初期化完了後のみ）
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('stageFilter', stageFilter);
    }
  }, [stageFilter, isInitialized]);

  // ソート設定の変更時に保存（初期化完了後のみ）
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('sortBy', sortBy);
    }
  }, [sortBy, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('sortOrder', sortOrder);
    }
  }, [sortOrder, isInitialized]);

  // アルゴリズムフィルタ設定の変更時に保存（初期化完了後のみ）
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('algorithmFilter', JSON.stringify(algorithmFilter));
    }
  }, [algorithmFilter, isInitialized]);

  // スクロール位置の復元
  useEffect(() => {
    const savedScrollPosition = localStorage.getItem('dashboardScrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition));
        // 一度復元したら削除（次回は最上部から開始）
        localStorage.removeItem('dashboardScrollPosition');
      }, 100);
    }
  }, [detectedStocks]); // データ読み込み完了後に実行

  // プログレス監視用
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (isAnalyzing) {
      // プログレス情報を定期的に取得
      intervalId = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/progress`);
          if (response.ok) {
            const progress = await response.json();
            setProgressCurrent(progress.current);
            setProgressTotal(progress.total);
            setCurrentStock(progress.current_stock);
            
            // 分析が完了したらintervalをクリア
            if (!progress.is_running && progress.current > 0) {
              setIsAnalyzing(false);
            }
          }
        } catch (error) {
          console.error('プログレス取得エラー:', error);
        }
      }, 1000); // 1秒ごとに更新
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAnalyzing]);

  const handleStartAnalysis = async () => {
    setIsAnalyzing(true);
    setProgressCurrent(0);
    setProgressTotal(0);
    setCurrentStock('');
    setDetectedStocks([]);
    setDetectionSummary(null);
    
    // 選択されたアルゴリズムが空の場合はデフォルトを使用
    if (selectedAlgorithms.length === 0) {
      alert('少なくとも1つのアルゴリズムを選択してください');
      setIsAnalyzing(false);
      return;
    }

    // 保存されたアルゴリズム設定を読み込み
    let algorithmSettings: {
      algorithmParams: { [key: string]: any };
      analysisParams: { analysis_period_days: number };
    } = {
      algorithmParams: {},
      analysisParams: { analysis_period_days: 30 }
    };
    
    try {
      const savedSettings = localStorage.getItem('algorithmSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        // algorithmParamsはアルゴリズムごとのパラメータを含むオブジェクト
        algorithmSettings.algorithmParams = settings.algorithmParams || {};
        algorithmSettings.analysisParams = settings.analysisParams || { analysis_period_days: 60 };
      }
    } catch (error) {
      console.error('設定読み込みエラー:', error);
    }
    
    try {
      // 複数アルゴリズム用のパラメータを構築
      const algorithmParams: { [key: string]: any } = {};

      selectedAlgorithms.forEach(algorithm => {
        // 各アルゴリズムのパラメータを個別に取得
        // 保存されていない場合はAlgorithmProviderからデフォルト値を取得
        let params = algorithmSettings.algorithmParams[algorithm];
        if (!params || Object.keys(params).length === 0) {
          const algoInfo = AlgorithmProvider.getAlgorithmByKey(algorithm);
          if (!algoInfo?.default_params) {
            console.error(`❌ アルゴリズム ${algorithm} のデフォルトパラメータが取得できません`);
            throw new Error(`アルゴリズム ${algorithm} のデフォルトパラメータが取得できません`);
          }
          params = algoInfo.default_params;
        }
        algorithmParams[algorithm] = params;
      });

      // デバッグログ
      console.log('🔍 送信するパラメータ:', {
        algorithm_names: selectedAlgorithms,
        algorithm_params: algorithmParams
      });

      // 複数アルゴリズム検出APIを呼び出し（必要日数はバックエンドで計算）
      const response = await fetch(`${API_BASE_URL}/detect/nasdaq/multi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          algorithm_keys: selectedAlgorithms,
          algorithm_params: algorithmParams
        })
      });
      
      if (!response.ok) {
        throw new Error(`API呼び出しエラー: ${response.status}`);
      }
      
      const result = await response.json();
      
      // 前回検出結果を取得（NEWバッジ判定用）
      const prevDetectedStocks = localStorage.getItem('detectedStocks');
      const previousStocks = prevDetectedStocks ? JSON.parse(prevDetectedStocks) : [];
      const previousSymbols = new Set(previousStocks.map((s: DetectedStock) => s.symbol));
      
      // 複数アルゴリズム検出結果をstateに反映（NEWフラグ付き）
      console.log('🔍 [Dashboard] バックエンドからの生レスポンス:', result);
      console.log('🔍 [Dashboard] result.results:', result.results);
      
      const stocksArray = Object.entries(result.results || {}).map(([, data]: [string, any]) => {
        console.log('🔍 [Dashboard] 個別銘柄データ:', data);
        console.log('🔍 [Dashboard] data.parameters:', data.parameters);
        console.log('🔍 [Dashboard] parametersの型:', typeof data.parameters);
        const isNewStock = !previousSymbols.has(data.symbol);
        const mappedStock = {
          symbol: data.symbol,
          company: data.company_name || `${data.symbol} Inc.`, // APIから会社名を取得、なければフォールバック
          confidence_score: data.confidence_score,
          change_date: data.change_date,
          stage: data.stage === '確定' ? '確定' as const : '兆候' as const,
          price_gain_percent: data.metrics?.price_gain_percent || 0, // metricsから取得、なければ0
          detection_reason: data.detection_reason,
          metrics: data.metrics,
          algorithm: data.algorithm,
          isNew: isNewStock,  // NEWフラグを追加
          current_price: data.current_price,  // 最新株価を追加
          parameters: data.parameters,  // 使用されたパラメータを追加
          required_days: data.required_days  // 検出に必要な日数を追加
        };
        console.log('🔍 [Dashboard] マッピング後のmappedStock:', mappedStock);
        console.log('🔍 [Dashboard] mappedStock.parameters:', mappedStock.parameters);
        return mappedStock;
      });
      
      setDetectedStocks(stocksArray);
      setDetectionSummary(result.summary);
      
      // 検出結果とタイムスタンプをlocalStorageに保存
      const currentTime = new Date().toLocaleString('ja-JP');
      setLastDetectionTime(currentTime);
      
      try {
        // 新しい検出結果を保存（前回結果は上書き）
        localStorage.setItem('detectedStocks', JSON.stringify(stocksArray));
        localStorage.setItem('detectionSummary', JSON.stringify(result.summary));
        localStorage.setItem('lastDetectionTime', currentTime);
        localStorage.setItem('selectedAlgorithms', JSON.stringify(selectedAlgorithms));
      } catch (error) {
        console.error('検出結果の保存エラー:', error);
      }
      
    } catch (error) {
      console.error('分析エラー:', error);
      alert(`分析エラーが発生しました: ${error}`);
    } finally {
      setIsAnalyzing(false);
      setProgressCurrent(0);
      setCurrentStock('');
    }
  };

  const handleStockClick = (stock: DetectedStock) => {
    // 現在のスクロール位置を保存
    localStorage.setItem('dashboardScrollPosition', window.pageYOffset.toString());
    // 選択した銘柄のデータを丸ごと保存
    localStorage.setItem('selectedStockData', JSON.stringify(stock));
    navigate(`/stocks/${stock.symbol}`);
  };

  // ソート関数
  const sortStocks = (stocks: DetectedStock[]) => {
    return [...stocks].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'confidence':
          comparison = b.confidence_score - a.confidence_score;
          break;
        case 'gain':
          comparison = (b.price_gain_percent || 0) - (a.price_gain_percent || 0);
          break;
        case 'price':
          comparison = (b.current_price || 0) - (a.current_price || 0);
          break;
        case 'date':
          comparison = new Date(b.change_date).getTime() - new Date(a.change_date).getTime();
          break;
        case 'symbol':
          comparison = a.symbol.localeCompare(b.symbol);
          break;
        default:
          return 0;
      }
      
      // 昇順・降順の適用
      return sortOrder === 'asc' ? -comparison : comparison;
    });
  };

  // フィルタ関数
  const filterStocks = (stocks: DetectedStock[]) => {
    let filteredStocks = stocks;
    
    // ステージフィルタ
    if (stageFilter !== 'all') {
      filteredStocks = filteredStocks.filter(stock => 
        stageFilter === 'confirmed' ? stock.stage === '確定' : stock.stage === '兆候'
      );
    }
    
    // アルゴリズムフィルタ
    if (algorithmFilter.length > 0) {
      filteredStocks = filteredStocks.filter(stock => {
        if (!stock.algorithm) {
          console.error('❌ [Dashboard] stock.algorithmが未定義:', stock);
          return false;
        }
        return algorithmFilter.includes(stock.algorithm);
      });
    }
    
    return filteredStocks;
  };

  // フィルタ後ソート済みの銘柄リスト
  const filteredStocks = filterStocks(detectedStocks);
  const displayedStocks = sortStocks(filteredStocks);

  // アルゴリズムごとの検出件数を計算
  const getAlgorithmCounts = () => {
    const counts: { [key: string]: number } = {};
    detectedStocks.forEach(stock => {
      if (stock.algorithm) {
        counts[stock.algorithm] = (counts[stock.algorithm] || 0) + 1;
      }
    });
    return counts;
  };

  // 現在表示中のユニーク銘柄数を計算
  const getUniqueSymbolCount = (stocks: DetectedStock[]) => {
    const uniqueSymbols = new Set(stocks.map(stock => stock.symbol));
    return uniqueSymbols.size;
  };

  const algorithmCounts = getAlgorithmCounts();
  const displayedCount = displayedStocks.length;
  const uniqueSymbolCount = getUniqueSymbolCount(displayedStocks);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>上昇銘柄検出</h2>
      </div>

      {/* データ期間表示と手動更新 */}
      <div className="data-info-section">
        <div className="data-range-info">
          <div className="data-range-header">
            <h3>データ期間情報</h3>
            <button 
              className="refresh-range-btn"
              onClick={handleRefreshDataRange}
              disabled={isRefreshingRange || isUpdatingData}
              title="期間情報のみを更新（高速）"
            >
              {isRefreshingRange ? '更新中...' : '🔄 期間情報更新'}
            </button>
          </div>
          {dataRangeError ? (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {dataRangeError}
            </div>
          ) : dataRange ? (
            <div className="data-range-details">
              {dataRange.oldest_date && dataRange.latest_date && (
                <div className="range-item">
                  <span className="range-label">データ期間:</span>
                  <span className="range-value">{dataRange.oldest_date} 〜 {dataRange.latest_date}</span>
                </div>
              )}
              <div className="range-item">
                <span className="range-label">期間:</span>
                <span className="range-value">{dataRange.days_covered}日間</span>
              </div>
              {dataRange.trading_days_available !== undefined && (
                <div className="range-item">
                  <span className="range-label">営業日数:</span>
                  <span className="range-value">
                    {dataRange.trading_days_available !== null 
                      ? `${dataRange.trading_days_available}営業日` 
                      : '取得中...'}
                    {dataRange.reference_symbol && (
                      <small style={{ marginLeft: '0.5rem', color: '#7f8c8d' }}>
                        ({dataRange.reference_symbol}ベース)
                      </small>
                    )}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="loading-message">データ期間情報を取得中...</div>
          )}
        </div>

        <div className="manual-update-section">
          <div className="data-range-header">
            <h3>データ更新</h3>
            <button 
              className="refresh-range-btn"
              onClick={handleManualDataUpdate}
              disabled={isUpdatingData || isAnalyzing}
              title="Stock Price Provider APIから最新データを取得（数分かかる場合があります）"
            >
              {isUpdatingData ? 'バックグラウンド処理中...' : '📥 最新データを取得'}
            </button>
          </div>
          <p className="update-description">
            Stock Price Provider APIから最新の株価データを取得します。処理は数分かかる場合があります。
          </p>
          {updateMessage && (
            <div className={`update-message ${updateMessage.includes('エラー') ? 'error' : 'success'}`}>
              {updateMessage}
            </div>
          )}
        </div>
      </div>

      <div className="summary-stats">
        <div className="analyze-section">
          <div className="button-group">
            <button
              onClick={handleStartAnalysis}
              disabled={isAnalyzing}
              className="analyze-button"
            >
              {isAnalyzing ? '分析実行中...' : '変化点検出開始'}
            </button>
            {lastDetectionTime && (
              <div className="last-detection-info">
                <span className="last-detection-label">最終検出日時:</span>
                <span className="last-detection-time">{lastDetectionTime}</span>
              </div>
            )}
          </div>

          {/* アルゴリズム選択チェックボックス */}
          <div className="algorithm-selection">
            <span className="selection-label">使用アルゴリズム:</span>
            <div className="algorithm-checkboxes">
              {algorithms.map(algo => (
                <label key={algo.key} className="algorithm-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedAlgorithms.includes(algo.key)}
                    onChange={(e) => handleAlgorithmChange(algo.key, e.target.checked)}
                  />
                  {algo.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="stats-group">
          <div className="stat-item">
            <span className="stat-label">検出銘柄数:</span>
            <span className="stat-value">{detectedStocks.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">処理時間:</span>
            <span className="stat-value">
              {detectionSummary ? `${detectionSummary.processing_time}秒` : '--'}
            </span>
          </div>
          {detectionSummary && (
            <div className="stat-item">
              <span className="stat-label">処理銘柄数:</span>
              <span className="stat-value">{detectionSummary.total_stocks}</span>
            </div>
          )}
        </div>
      </div>

      {/* 更新中のオーバーレイ */}
      {isUpdatingData && (
        <div className="update-overlay">
          <div className="update-overlay-content">
            <div className="update-spinner"></div>
            <h3>バックグラウンドでデータ更新中</h3>
            <p>Stock Price Provider APIから最新データを取得しています</p>
            <p><small>処理完了まで数分かかる場合があります</small></p>
            {updateMessage && (
              <div className="update-status">
                {updateMessage}
              </div>
            )}
          </div>
        </div>
      )}

      {/* プログレスバー */}
      <ProgressBar
        current={progressCurrent}
        total={progressTotal}
        isActive={isAnalyzing}
        currentStock={currentStock}
      />

      <div className="detected-stocks">
        <div className="controls-row">
          {/* セクションタイトル（左端） */}
          <h3 className="section-title">検出された銘柄</h3>

          {/* 統計情報ボックス */}
          <div className="stock-count-box">
            <div className="count-row">
              <span className="count-label">表示中:</span>
              <span className="count-value">{displayedCount}件</span>
            </div>
            <div className="count-row">
              <span className="count-label">銘柄数:</span>
              <span className="count-value">{uniqueSymbolCount}銘柄</span>
            </div>
          </div>

          {/* 段階フィルタ */}
          <div className="filter-section">
            <span className="filter-label">段階フィルタ:</span>
            <div className="filter-options">
              <label>
                <input
                  type="radio"
                  name="stage-filter"
                  value="all"
                  checked={stageFilter === 'all'}
                  onChange={(e) => setStageFilter(e.target.value as typeof stageFilter)}
                />
                すべて
              </label>
              <label>
                <input
                  type="radio"
                  name="stage-filter"
                  value="confirmed"
                  checked={stageFilter === 'confirmed'}
                  onChange={(e) => setStageFilter(e.target.value as typeof stageFilter)}
                />
                確定のみ
              </label>
              <label>
                <input
                  type="radio"
                  name="stage-filter"
                  value="indication"
                  checked={stageFilter === 'indication'}
                  onChange={(e) => setStageFilter(e.target.value as typeof stageFilter)}
                />
                兆候のみ
              </label>
            </div>
          </div>

          {/* アルゴリズムフィルタ */}
          <div className="filter-section">
            <span className="filter-label">アルゴリズムフィルタ:</span>
            <div className="filter-options">
              {algorithms.map(algo => {
                const count = algorithmCounts[algo.key] || 0;
                return (
                  <label key={algo.key}>
                    <input
                      type="checkbox"
                      checked={algorithmFilter.includes(algo.key)}
                      onChange={(e) => handleAlgorithmFilterChange(algo.key, e.target.checked)}
                    />
                    {algo.name} ({count})
                  </label>
                );
              })}
            </div>
          </div>

          {/* 並び替えコントロール（右端） */}
          <div className="sort-section">
            <label htmlFor="sort-select">並び替え:</label>
            <select
              id="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="sort-select"
            >
              <option value="confidence">信頼度</option>
              <option value="gain">上昇率</option>
              <option value="price">株価</option>
              <option value="date">検出日時</option>
              <option value="symbol">銘柄コード</option>
            </select>

            <div className="sort-order-controls">
              <label>
                <input
                  type="radio"
                  name="sort-order"
                  value="desc"
                  checked={sortOrder === 'desc'}
                  onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
                />
                降順
              </label>
              <label>
                <input
                  type="radio"
                  name="sort-order"
                  value="asc"
                  checked={sortOrder === 'asc'}
                  onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
                />
                昇順
              </label>
            </div>
          </div>
        </div>

        <div className="stock-list">
          {displayedStocks.length === 0 ? (
            <div>表示する銘柄がありません</div>
          ) : (
          displayedStocks.map((stock, index) => {
            // お気に入り用スナップショットを作成
            const algorithmInfo = AlgorithmProvider.getAlgorithmByKey(stock.algorithm || '');
            const favoriteSnapshot: FavoriteSnapshot = {
              symbol: stock.symbol,
              company_name: stock.company || stock.symbol,
              detection_date: new Date().toISOString(),
              detection_result: {
                symbol: stock.symbol,
                company_name: stock.company || stock.symbol,
                detection_date: new Date().toISOString(),
                change_point_date: stock.change_date,
                confidence_score: stock.confidence_score,
                algorithm_id: stock.algorithm || '',
                algorithm_name: algorithmInfo?.display_name_ja || stock.algorithm || '',
                parameters: stock.parameters || {},
                detection_reason: stock.detection_reason,
                metrics: stock.metrics,
                required_days: stock.required_days
              },
              added_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            return (
              <div
                key={`${stock.symbol}-${index}`}
                className="stock-item"
              >
                <div className="stock-item-header">
                  <div className="stock-item-favorite">
                    <FavoriteButton snapshot={favoriteSnapshot} size="medium" />
                  </div>
                  <div className="stock-item-content" onClick={() => handleStockClick(stock)}>
                    <div className="stock-basic">
                      <span className="stock-symbol">{stock.symbol}</span>
                      {stock.isNew && <span className="new-badge">NEW</span>}
                      <span className="stock-company">{stock.company}</span>
                    </div>
                    <div className="stock-metrics">
                      <div className="confidence">
                        信頼度: <strong>{Math.floor(stock.confidence_score)}%</strong>
                      </div>
                      <div className={`stage ${stock.stage === '確定' ? 'confirmed' : 'indication'}`}>
                        {stock.stage === '確定' ? '確定' : '兆候'}
                      </div>
                      <div className="algorithm">
                        アルゴリズム: <strong>{stock.algorithm ? getAlgorithmDisplayName(stock.algorithm) : '⚠️ 未定義'}</strong>
                      </div>
                      <div className="gain">
                        上昇率: <strong>{stock.price_gain_percent !== null && stock.price_gain_percent !== undefined ? (stock.price_gain_percent >= 0 ? '+' : '') + stock.price_gain_percent.toFixed(1) + '%' : '0.0%'}</strong>
                      </div>
                      <div className="current-price">
                        株価: <strong>${stock.current_price !== null && stock.current_price !== undefined ? stock.current_price.toFixed(2) : 'N/A'}</strong>
                      </div>
                      <div className="change-date">
                        変化点: {stock.change_date}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;